import React, { useEffect, useMemo, useState, type JSX } from 'react';
import { FlightTooltip, getIcon } from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { Preset } from 'components/Shared/DateRangePicker';
import { ReactComponent as AndroidGray } from 'assets/icons/android_gray.svg';
import { ReactComponent as IosGray } from 'assets/icons/ios_gray.svg';
import { ReactComponent as WebGray } from 'assets/icons/web_gray.svg';
import { ReactComponent as IosBlue } from 'assets/icons/ios_blue.svg';
import { ReactComponent as AndroidGreen } from 'assets/icons/android_green.svg';
import { ReactComponent as WebBlue } from 'assets/icons/web_blue.svg';

import './AnalyticsDashboard.scss';

import { GraphData, AnalyticsOverview as TAnalyticsOverview } from 'components/Analytics/types';
import AnalyticsErrorState from 'pages/Analytics/components/AnalyticsErrorState/AnalyticsErrorState';
import AnalyticsExperiencesAPI from 'services/api/analytics-experiences.api';
import EngagementTables from './Engagement/EngagementTables';
import ExportEngagement from './Engagement/ExportEngagement';
import { useQuery } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import PieGraphDevice, { PieGraphDeviceSkeleton } from 'components/Shared/Graphs/PieGraphDevice';
import { AdvancedDateRange } from 'pages/Analytics/types';
import useDelayedLoadingState from 'hooks/useDelayedLoading';
import { epochToDateTimeString, formatPercentage, intlNumberFormat } from 'helpers/common.helper';
import { DATE_FORMAT_OPTIONS } from './constants';

const CLASS_ANALYTICS_OVERVIEW_DEFAULT = 'analytics-dashboard';

const DATERANGE_PRESETS: Preset[] = ['today', 'last7', 'last30', 'last60', 'last90', 'custom'];

const ENGAGEMENT = `${CLASS_ANALYTICS_OVERVIEW_DEFAULT}__engagement`;
const ENGAGEMENT_CONTAINER = {
  SELF: `${ENGAGEMENT}__container`,
  HEADER: `${ENGAGEMENT}__header`,
};

const METRICS = `${CLASS_ANALYTICS_OVERVIEW_DEFAULT}__metrics`;
const METRIC_LEVEL = `${METRICS}__metric-level`;

const defaultGraphData = [
  {
    id: 'ios',
    label: '50.0%',
    value: 50,
    color: '#056AFF',
  },
  {
    id: 'android',
    label: '50.0%',
    value: 50,
    color: '#00AB50',
  },
  {
    id: 'other',
    label: '0%',
    value: 0,
    color: '#72DAFF',
  },
];

type MetricLevelProps = {
  title: string;
  children: JSX.Element | JSX.Element[];
  header?: JSX.Element | JSX.Element[];
};

// Lines inside the Metric objects
const MetricLevel = ({ title, children, header }: MetricLevelProps) => {
  const METRIC_TITLE_CONTAINER = `${METRIC_LEVEL}__metric-level-title-container`;
  const METRIC_TITLE_TEXT = `${METRIC_TITLE_CONTAINER}__text`;
  const METRIC_DATA_CONTAINER = `${METRIC_LEVEL}__metric-data-container`;
  return (
    <div className={METRIC_LEVEL}>
      <div className={METRIC_TITLE_CONTAINER}>
        <div className={METRIC_TITLE_TEXT}>{title}</div>
        {header && <div> {header} </div>}
      </div>
      <div className={METRIC_DATA_CONTAINER}>{children}</div>
    </div>
  );
};

type GridRowProps = {
  className: string;
  children: JSX.Element | JSX.Element[];
};

const GridLayout = ({ children, className }: GridRowProps) => {
  return (
    <div style={{ display: 'grid' }} className={className}>
      {children}
    </div>
  );
};

type MetricProps = {
  title: string;
  children: JSX.Element | JSX.Element[];
  rightSide?: JSX.Element | JSX.Element[];
  header?: JSX.Element | JSX.Element[];
  tooltip?: string;
};

// Top level object to hold the Metric Levels
const Metric = ({ title, children, rightSide, header, tooltip }: MetricProps) => {
  const METRIC = `${METRIC_LEVEL}__metric`;
  const METRIC_LEFT = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_RIGHT = `${METRIC_LEVEL}__metric__right-side`;
  const METRIC_TITLE_CONTAINER = `${METRIC}__metric-title-container`;
  const METRIC_TITLE = `${METRIC_TITLE_CONTAINER}__text`;
  const METRIC_TITLE_INFO_ICON = `${METRIC_TITLE_CONTAINER}__info-icon`;

  return (
    <div className={METRIC}>
      <div className={METRIC_LEFT}>
        <div className={METRIC_TITLE_CONTAINER}>
          <div className={METRIC_TITLE}>{title}</div>
          {tooltip && (
            // Open to the right (not centered above the icon) so the popup never
            // overlaps the fixed nav sidebar on the left. The page lives inside
            // a `.route-content` stacking context (z-index: 1) that sits below
            // the nav sidebar (z-index: 2), so an inline tooltip can't win on
            // z-index alone — avoiding the overlap is what keeps it visible.
            <FlightTooltip description={tooltip} direction="right" maxWidth="240px">
              {getIcon('infoStroke', {
                className: METRIC_TITLE_INFO_ICON,
                width: '14px',
                height: '14px',
                fill: '#82899b',
              })}
            </FlightTooltip>
          )}
          {header && <div>{header}</div>}
        </div>
        {children}
      </div>
      {rightSide && <div className={METRIC_RIGHT}>{rightSide}</div>}
    </div>
  );
};

type MetricValueProps = {
  value: string;
  withDivier?: boolean | null;
  children?: undefined | JSX.Element | JSX.Element;
};

const MetricValue = ({ value, children }: MetricValueProps) => {
  const METRIC = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_VALUE_CONTAINER = `${METRIC}__metric-value-container`;
  const METRIC_VALUE = `${METRIC_VALUE_CONTAINER}__metric-value`;
  return (
    <div className={METRIC_VALUE_CONTAINER}>
      <div className={METRIC_VALUE}>{value}</div>
      {children}
    </div>
  );
};

type SubMetricValue = {
  text: string;
  svg?: string;
  chg?: number;
  className?: string;
};

const SubMetricValue = ({ text, svg, chg, className }: SubMetricValue) => {
  const METRIC = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_VALUE_CONTAINER = `${METRIC}__metric-value-container`;
  const SUB_METRIC_VALUE = `${METRIC_VALUE_CONTAINER}__sub-metric-value`;

  const SUB_METRIC_CLASSNAME = `${SUB_METRIC_VALUE}  ${className ? SUB_METRIC_VALUE + className : ''}`;
  return (
    <div className={`${SUB_METRIC_CLASSNAME}`}>
      {(svg || chg) && (
        <div className={`${SUB_METRIC_CLASSNAME}__indicator`}>
          {svg ? getIcon(svg, {}) : chg ? <div> {chg.toFixed(0) + '%'} </div> : <></>}
        </div>
      )}
      <div className={`${SUB_METRIC_CLASSNAME}__text`}>{text}</div>
    </div>
  );
};

type DeviceIndicatorProps = {
  svg: JSX.Element;
  name: string;
};

const DeviceIndicator = ({ svg, name }: DeviceIndicatorProps) => {
  const METRIC = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_VALUE_CONTAINER = `${METRIC}__metric-value-container`;
  const DEVICE_VALUE = `${METRIC_VALUE_CONTAINER}__device-indicator`;

  return (
    <div className={`${DEVICE_VALUE}`}>
      <div className={`${DEVICE_VALUE}__indicator`}>{svg}</div>
      <div className={`${DEVICE_VALUE}__text`}>{name}</div>
    </div>
  );
};

const SubMetricValueDeltaChange = (props: any, labelCopy: string): JSX.Element => {
  if (props?.change > 0) {
    return (
      <SubMetricValue
        text={`${props.change.toFixed(2) ?? 0}% ${labelCopy}`}
        svg="baselineArrowUp"
        className={'__positive-value'}
      />
    );
  } else if (props?.change == 0 || !props.change) {
    return <SubMetricValue text={`No changes ${labelCopy}`} className={'__nutural-value'} />;
  } else {
    return (
      <SubMetricValue
        text={`${Math.abs(props.change.toFixed(2))}% ${labelCopy}`}
        svg="baselineArrowDown"
        className={'__negitive-value'}
      />
    );
  }
};

const DeviceOptInBreakDownSkeleton = ({ rows }: { rows: number }): JSX.Element => {
  const METRIC = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_VALUE_CONTAINER = `${METRIC}__metric-value-container`;
  const DEVICE_STACK = `${METRIC_VALUE_CONTAINER}__device-indicator-stack`;
  const DEVICE_ROW = `${DEVICE_STACK}__device-row`;

  return (
    <div className={`${DEVICE_STACK}`}>
      {Array.from({ length: rows }).map((_, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <div className={DEVICE_ROW}>
              <hr />
            </div>
          )}
          <div className={DEVICE_ROW}>
            <Skeleton width={64} height={14} />
            <Skeleton width={80} height={14} />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

type SubMetricValuePushDeviceBreakDownProps = {
  ios: number;
  android: number;
  isLoading?: boolean;
  platformSplit?: {
    ios: number;
    android: number;
    web: number;
    other: number;
  };
};

const SubMetricValuePushDeviceBreakDown = ({
  ios,
  android,
  isLoading,
  platformSplit,
}: SubMetricValuePushDeviceBreakDownProps): JSX.Element => {
  const iosOptIn = platformSplit?.ios ?? 0;
  const androidOptIn = platformSplit?.android ?? 0;
  const METRIC = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_VALUE_CONTAINER = `${METRIC}__metric-value-container`;
  const DEVICE_STACK = `${METRIC_VALUE_CONTAINER}__device-indicator-stack`;
  const DEVICE_ROW = `${DEVICE_STACK}__device-row`;
  const DEVICE_ROW_VALUE_CONTAINER = `${DEVICE_STACK}__device-row-value-container`;
  const DEVICE_ROW_VALUE = `${DEVICE_ROW_VALUE_CONTAINER}__value`;
  const DEVICE_ROW_VALUE_PERCENTAGE = `${DEVICE_ROW_VALUE}__value-percentage`;

  if (isLoading) {
    return <DeviceOptInBreakDownSkeleton rows={2} />;
  }

  return (
    <div className={`${DEVICE_STACK}`}>
      <div className={DEVICE_ROW}>
        <DeviceIndicator svg={<IosBlue />} name="iOS" />
        <div className={DEVICE_ROW_VALUE_CONTAINER}>
          <div className={DEVICE_ROW_VALUE}>
            <span>
              {`${intlNumberFormat(ios)} (`}
              <span className={DEVICE_ROW_VALUE_PERCENTAGE}>
                {iosOptIn ? `${formatPercentage(iosOptIn * 100)}%` : `0%`}
              </span>
              <span>)</span>
            </span>
          </div>
        </div>
      </div>
      <div className={DEVICE_ROW}>
        <hr />
      </div>
      <div className={DEVICE_ROW}>
        <DeviceIndicator svg={<AndroidGreen />} name="Android" />
        <div className={DEVICE_ROW_VALUE_CONTAINER}>
          <div className={DEVICE_ROW_VALUE}>
            <span>
              {`${intlNumberFormat(android)} (`}
              <span className={DEVICE_ROW_VALUE_PERCENTAGE}>
                {androidOptIn ? `${formatPercentage(androidOptIn * 100)}%` : `0%`}
              </span>
              <span>)</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

type SubMetricValueLocationOptInBreakDownProps = {
  ios: number;
  android: number;
  other: number;
  totalIOSDevices: number;
  totalAndroidDevices: number;
  totalOtherDevices: number;
  isLoading?: boolean;
};

const SubMetricValueLocationOptInBreakDown = ({
  ios,
  android,
  other,
  totalIOSDevices,
  totalAndroidDevices,
  totalOtherDevices,
  isLoading,
}: SubMetricValueLocationOptInBreakDownProps): JSX.Element => {
  const METRIC = `${METRIC_LEVEL}__metric__left-side`;
  const METRIC_VALUE_CONTAINER = `${METRIC}__metric-value-container`;
  const DEVICE_STACK = `${METRIC_VALUE_CONTAINER}__device-indicator-stack`;
  const DEVICE_ROW = `${DEVICE_STACK}__device-row`;
  const DEVICE_ROW_VALUE_CONTAINER = `${DEVICE_STACK}__device-row-value-container`;
  const DEVICE_ROW_VALUE = `${DEVICE_ROW_VALUE_CONTAINER}__value`;
  const DEVICE_ROW_VALUE_PERCENTAGE = `${DEVICE_ROW_VALUE}__value-percentage`;

  if (isLoading) {
    return <DeviceOptInBreakDownSkeleton rows={3} />;
  }

  return (
    <div className={`${DEVICE_STACK}`}>
      <div className={DEVICE_ROW}>
        <DeviceIndicator svg={<IosBlue />} name="iOS" />
        <div className={DEVICE_ROW_VALUE_CONTAINER}>
          <div className={DEVICE_ROW_VALUE}>
            <span>
              {`${intlNumberFormat(ios)}${ios > totalIOSDevices ? '*' : ''} (`}
              <span className={DEVICE_ROW_VALUE_PERCENTAGE}>
                {totalIOSDevices ? `${formatPercentage((ios / totalIOSDevices) * 100)}%` : `0%`}
              </span>
              <span>)</span>
            </span>
          </div>
        </div>
      </div>
      <div className={DEVICE_ROW}>
        <hr />
      </div>
      <div className={DEVICE_ROW}>
        <DeviceIndicator svg={<AndroidGreen />} name="Android" />
        <div className={DEVICE_ROW_VALUE_CONTAINER}>
          <div className={DEVICE_ROW_VALUE}>
            <span>
              {`${intlNumberFormat(android)}${android > totalAndroidDevices ? '*' : ''} (`}
              <span className={DEVICE_ROW_VALUE_PERCENTAGE}>
                {totalAndroidDevices ? `${formatPercentage((android / totalAndroidDevices) * 100)}%` : `0%`}
              </span>
              <span>)</span>
            </span>
          </div>
        </div>
      </div>
      <div className={DEVICE_ROW}>
        <hr />
      </div>
      <div className={DEVICE_ROW}>
        <DeviceIndicator svg={<WebBlue />} name="Other" />
        <div className={DEVICE_ROW_VALUE_CONTAINER}>
          <div className={DEVICE_ROW_VALUE}>
            <span>
              {`${intlNumberFormat(other)}${other > totalOtherDevices ? '*' : ''} (`}
              <span className={DEVICE_ROW_VALUE_PERCENTAGE}>
                {totalOtherDevices ? `${formatPercentage((other / totalOtherDevices) * 100)}%` : `0%`}
              </span>
              <span>)</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AnalyticsDashboard() {
  const analyticsExperiencesApi = useMemo(() => new AnalyticsExperiencesAPI(), []);

  const [dateRange, setDateRange] = useState<AdvancedDateRange>(() => {
    const startOfToday = new Date();
    const endOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    endOfToday.setHours(23, 59, 59, 999);
    return [startOfToday, endOfToday, 0, 'day'];
  });

  const handleDateRangeChange = (newDate: AdvancedDateRange) => {
    setDateRange(newDate);
  };

  const [totalDevices, setTotalDevices] = useState<GraphData[]>([]);
  const [totalClickThroughRate, setTotalClickThroughRate] = useState<GraphData[]>([]);

  const {
    isLoading: isAOLoading,
    isFetching: isAOFetching,
    isError: isOverviewExperiencesError,
    refetch,
  } = useQuery({
    queryKey: ['analytics-overview', dateRange],
    queryFn: async () => {
      const endOfDay = new Date(dateRange[1] ?? new Date());
      if (dateRange.length < 3 || (dateRange.length >= 3 && dateRange[2] === 0)) {
        // Known interval - no custom or single day
        endOfDay.setHours(23, 59, 59, 999);
      }

      const fetchedOverview = await analyticsExperiencesApi.getOverviewExperiences({
        startTime: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
        endTime: Math.trunc((endOfDay?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
      });

      setOverviewExperiencesToRender(fetchedOverview);
      return fetchedOverview;
    },
    staleTime: 0,
    enabled: dateRange[0] !== null && dateRange[1] !== null,
    retry: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const prepareCopyForLabelBasedOn = (range: AdvancedDateRange): string => {
    let copyForLabel = ``;
    const days = range[2];

    if (days !== undefined) {
      // predefinied range
      if (days == 0 || days == 1) {
        copyForLabel = 'over the past day';
      } else {
        copyForLabel = `over the past ${days} days`;
      }
    } else {
      // Custom range
      const startDate = range[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endDate = range[1]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      copyForLabel = `from ${startDate ?? 'Invalid data'} to ${endDate ?? 'Invalid date'}`;
    }

    return copyForLabel;
  };

  const [overviewExperiencesToRender, setOverviewExperiencesToRender] = useState<TAnalyticsOverview>();
  const isOverviewLoading = useDelayedLoadingState((isAOLoading || isAOFetching) && !overviewExperiencesToRender);
  // The "Overview (All experiences)" section is driven by the date-range picker,
  // so it should also show the skeleton on every refetch (timeframe change),
  // not just the initial load.
  const isExperiencesLoading = useDelayedLoadingState(isAOLoading || isAOFetching);
  const locationOptIn = overviewExperiencesToRender?.locationOptIn;
  const locationOptInTotal = locationOptIn
    ? locationOptIn.web + locationOptIn.other + locationOptIn.android + locationOptIn.ios
    : 0;

  const HEADER_DATE_CLASS = `${CLASS_ANALYTICS_OVERVIEW_DEFAULT}__header-date`;
  const HEADER_DATE_TITLE_CLASS = `${HEADER_DATE_CLASS}__header__date__title`;
  const HEADER_DATE_DROP_DOWN_CLASS = `${HEADER_DATE_CLASS}__header__date__dropdown`;
  const isOverallPresetSelected = dateRange[2] !== undefined || dateRange[3] === 'allTime';
  // Rendered inline (not as a nested component) so React doesn't remount the
  // DateRangePicker on every AnalyticsDashboard re-render — which would reset
  // the picker's internal Custom-preset flow state and auto-close on selection.
  const overallExperiencesDatePicker = (
    <div className={HEADER_DATE_CLASS}>
      <div className={HEADER_DATE_TITLE_CLASS}>
        {isOverallPresetSelected &&
          (dateRange[2] === 0
            ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
            : `${epochToDateTimeString(dateRange[0] || 0, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
                dateRange[1] || 0,
                'en-US',
                DATE_FORMAT_OPTIONS,
              )}`)}
      </div>
      <DateRangePicker
        className={HEADER_DATE_DROP_DOWN_CLASS}
        value={dateRange}
        onChange={handleDateRangeChange}
        maxDate={new Date()}
        presets={DATERANGE_PRESETS}
        ariaLabel="Select a date range for overall experiences"
      />
    </div>
  );

  useEffect(() => {
    document.title = 'Analytics | Experience Studio @ Flybits';
  }, []);

  useEffect(() => {
    if (window.location.hash === '#updated') {
      refetch();
      window.location.hash = '';
    }
    // eslint-disable-next-line
  }, [window.location.hash]);

  // Create Device breakout graph based on platform
  useEffect(() => {
    const webNumber = overviewExperiencesToRender?.totalDevices?.platformSplit?.web ?? 0;

    const graphData = Object.entries(overviewExperiencesToRender?.totalDevices?.platformSplit ?? {})
      .map(([name, decimal]) => {
        const value = decimal * 100;
        switch (name) {
          case 'ios':
            return {
              id: 'ios',
              label: value.toFixed(1).concat('%'),
              value: value.toFixed(1),
              color: '#056AFF',
            };
          case 'android':
            return {
              id: 'android',
              label: value.toFixed(1).concat('%'),
              value: value.toFixed(1),
              color: '#00AB50',
            };
          case 'other':
            const otherValue = value + webNumber * 100;
            return {
              id: 'other',
              label: otherValue.toFixed(1).concat('%'),
              value: otherValue.toFixed(2),
              color: '#72DAFF',
            };
          default:
            return {};
        }
      })
      .filter((e) => Object.entries(e).length > 0) as unknown as GraphData[];

    setTotalDevices(graphData.length > 0 ? graphData : defaultGraphData);
  }, [overviewExperiencesToRender]);

  useEffect(() => {
    const webNumber = overviewExperiencesToRender?.clickThroughRate?.platformSplit?.web ?? 0;

    const graphData = Object.entries(overviewExperiencesToRender?.clickThroughRate?.platformSplit ?? {})
      .map(([name, decimal]) => {
        if (!decimal) return {};
        const value = decimal;
        switch (name) {
          case 'ios':
            return {
              id: 'ios',
              label: value.toFixed(1).concat('%'),
              value: value.toFixed(2),
              color: '#056AFF',
            };
          case 'android':
            return {
              id: 'android',
              label: value.toFixed(1).concat('%'),
              value: value.toFixed(2),
              color: '#00AB50',
            };
          case 'other':
            const webAndOtherValue = value + webNumber;
            return {
              id: 'other',
              label: webAndOtherValue.toFixed(1).concat('%'),
              value: webAndOtherValue.toFixed(2),
              color: '#72DAFF',
            };
          default:
            return {};
        }
      })
      .filter((e) => Object.entries(e).length > 0) as unknown as GraphData[];

    setTotalClickThroughRate(graphData.length > 0 ? graphData : defaultGraphData);
  }, [overviewExperiencesToRender]);

  return (
    <div>
      <div className={CLASS_ANALYTICS_OVERVIEW_DEFAULT}>
        <div className={METRICS}>
          {isOverviewExperiencesError ? (
            <AnalyticsErrorState onRetry={() => refetch()} isRetrying={isAOFetching} />
          ) : (
            <>
              <MetricLevel title="User level">
                <GridLayout className={`${METRIC_LEVEL}__overload-grid-row ${METRIC_LEVEL}__overload-grid-row--user`}>
                  <Metric
                    title="Total users"
                    tooltip="The number of unique users who are reachable via Flybits experiences."
                  >
                    {isOverviewLoading ? (
                      <Skeleton width={40} height={30} />
                    ) : (
                      <MetricValue
                        value={intlNumberFormat(overviewExperiencesToRender?.totalUsers?.value || 0)}
                      ></MetricValue>
                    )}
                  </Metric>
                  <Metric
                    title="Total users opt in to push notifications"
                    tooltip="The number of unique users that have given permission to send them push notifications."
                  >
                    {isOverviewLoading ? (
                      <Skeleton width={40} height={30} />
                    ) : (
                      <MetricValue
                        value={intlNumberFormat(
                          (overviewExperiencesToRender?.pushOptIn?.ios ?? 0) +
                            (overviewExperiencesToRender?.pushOptIn?.android ?? 0) +
                            (overviewExperiencesToRender?.pushOptIn?.other ?? 0) +
                            (overviewExperiencesToRender?.pushOptIn?.web ?? 0),
                        )}
                      >
                        <SubMetricValue
                          text={`of total users`}
                          chg={
                            overviewExperiencesToRender?.totalUsers?.value
                              ? (((overviewExperiencesToRender?.pushOptIn?.ios ?? 0) +
                                  (overviewExperiencesToRender?.pushOptIn?.android ?? 0) +
                                  (overviewExperiencesToRender?.pushOptIn?.other ?? 0) +
                                  (overviewExperiencesToRender?.pushOptIn?.web ?? 0)) /
                                  overviewExperiencesToRender?.totalUsers?.value) *
                                100
                              : undefined
                          }
                          className={'__nutural-value'}
                        />
                      </MetricValue>
                    )}
                  </Metric>
                  <Metric
                    title="Total users opted in to location"
                    tooltip="The number of unique users that have given permission to use their location for Flybits experiences."
                  >
                    {isOverviewLoading ? (
                      <Skeleton width={40} height={30} />
                    ) : (
                      <MetricValue value={intlNumberFormat(locationOptInTotal)}>
                        <SubMetricValue
                          text={`of total users`}
                          chg={
                            overviewExperiencesToRender?.totalUsers?.value
                              ? (((overviewExperiencesToRender?.locationOptIn?.ios ?? 0) +
                                  (overviewExperiencesToRender?.locationOptIn?.android ?? 0) +
                                  (overviewExperiencesToRender?.locationOptIn?.web ?? 0) +
                                  (overviewExperiencesToRender?.locationOptIn?.other ?? 0)) /
                                  overviewExperiencesToRender?.totalUsers?.value) *
                                100
                              : undefined
                          }
                          className={'__nutural-value'}
                        />
                      </MetricValue>
                    )}
                  </Metric>
                </GridLayout>
              </MetricLevel>
              <MetricLevel title="Device level">
                <GridLayout className={`${METRIC_LEVEL}__two-by-two-grid ${METRIC_LEVEL}__two-by-two-grid--device`}>
                  <Metric
                    title="Total devices"
                    tooltip="The total number of devices (phones, tablets, etc.) connected to your audience."
                  >
                    {isOverviewLoading ? (
                      <Skeleton width={40} height={30} />
                    ) : (
                      <MetricValue value={intlNumberFormat(overviewExperiencesToRender?.totalDevices?.value ?? 0)} />
                    )}
                  </Metric>
                  <Metric
                    title="Device breakout based on platform"
                    tooltip="The number of devices grouped by platform."
                    rightSide={
                      isOverviewLoading ? (
                        <PieGraphDeviceSkeleton legendCount={3} />
                      ) : (
                        <PieGraphDevice data={totalDevices} />
                      )
                    }
                  >
                    <GridLayout className={`${METRIC_LEVEL}__device-based-on-platform`}>
                      {isOverviewLoading ? (
                        <Skeleton width={40} height={30} />
                      ) : (
                        <MetricValue
                          value={intlNumberFormat(
                            overviewExperiencesToRender && overviewExperiencesToRender.totalDevices
                              ? overviewExperiencesToRender.totalDevices.value *
                                  overviewExperiencesToRender.totalDevices.platformSplit.ios
                              : 0,
                          )}
                        >
                          <DeviceIndicator svg={<IosGray />} name="iOS" />
                        </MetricValue>
                      )}
                      <hr />
                      {isOverviewLoading ? (
                        <Skeleton width={40} height={30} />
                      ) : (
                        <MetricValue
                          value={intlNumberFormat(
                            overviewExperiencesToRender && overviewExperiencesToRender.totalDevices
                              ? overviewExperiencesToRender.totalDevices.value *
                                  overviewExperiencesToRender.totalDevices.platformSplit.android
                              : 0,
                          )}
                        >
                          <DeviceIndicator svg={<AndroidGray />} name="Android" />
                        </MetricValue>
                      )}
                      <hr />
                      {isOverviewLoading ? (
                        <Skeleton width={40} height={30} />
                      ) : (
                        <MetricValue
                          value={intlNumberFormat(
                            overviewExperiencesToRender && overviewExperiencesToRender.totalDevices
                              ? overviewExperiencesToRender.totalDevices.value -
                                  (overviewExperiencesToRender.totalDevices.value *
                                    overviewExperiencesToRender.totalDevices.platformSplit.ios +
                                    overviewExperiencesToRender.totalDevices.value *
                                      overviewExperiencesToRender.totalDevices.platformSplit.android)
                              : 0,
                          )}
                        >
                          <DeviceIndicator svg={<WebGray />} name="Web & other" />
                        </MetricValue>
                      )}
                    </GridLayout>
                  </Metric>
                  <Metric
                    title="Device push opt ins based on platform"
                    tooltip="The breakdown of how push notification permissions are split across platforms (e.g., iOS vs Android)."
                  >
                    <SubMetricValuePushDeviceBreakDown
                      isLoading={isOverviewLoading}
                      ios={overviewExperiencesToRender?.pushOptIn?.ios ?? 0}
                      android={overviewExperiencesToRender?.pushOptIn?.android ?? 0}
                      platformSplit={overviewExperiencesToRender?.pushOptIn?.platformSplit}
                    />
                  </Metric>
                  <Metric
                    title="Location opt ins based on platform"
                    tooltip="How location permissions are distributed across platforms"
                  >
                    <SubMetricValueLocationOptInBreakDown
                      isLoading={isOverviewLoading}
                      ios={overviewExperiencesToRender?.locationOptIn?.ios ?? 0}
                      totalIOSDevices={
                        overviewExperiencesToRender && overviewExperiencesToRender.totalDevices
                          ? overviewExperiencesToRender.totalDevices.value *
                            overviewExperiencesToRender.totalDevices.platformSplit.ios
                          : 0
                      }
                      android={overviewExperiencesToRender?.locationOptIn?.android ?? 0}
                      totalAndroidDevices={
                        overviewExperiencesToRender && overviewExperiencesToRender.totalDevices
                          ? overviewExperiencesToRender.totalDevices.value *
                            overviewExperiencesToRender.totalDevices.platformSplit.android
                          : 0
                      }
                      other={
                        (overviewExperiencesToRender?.locationOptIn?.other ?? 0) +
                        (overviewExperiencesToRender?.locationOptIn?.web ?? 0)
                      }
                      totalOtherDevices={
                        overviewExperiencesToRender && overviewExperiencesToRender.totalDevices
                          ? overviewExperiencesToRender.totalDevices.value -
                            (overviewExperiencesToRender.totalDevices.value *
                              overviewExperiencesToRender.totalDevices.platformSplit.ios +
                              overviewExperiencesToRender.totalDevices.value *
                                overviewExperiencesToRender.totalDevices.platformSplit.android)
                          : 0
                      }
                    />
                  </Metric>
                </GridLayout>
              </MetricLevel>
              <MetricLevel title="Overview (All experiences)" header={overallExperiencesDatePicker}>
                <GridLayout className={`${METRIC_LEVEL}__two-by-two-grid ${METRIC_LEVEL}__two-by-two-grid--overview`}>
                  <Metric
                    title="Total experiences launched"
                    tooltip="The total number of experiences that have been launched."
                  >
                    {isExperiencesLoading ? (
                      <Skeleton width={40} height={30} />
                    ) : (
                      <MetricValue
                        value={intlNumberFormat(overviewExperiencesToRender?.experiencesLaunched?.value || 0)}
                      >
                        {SubMetricValueDeltaChange(
                          overviewExperiencesToRender?.experiencesLaunched,
                          prepareCopyForLabelBasedOn(dateRange),
                        )}
                      </MetricValue>
                    )}
                  </Metric>
                  <Metric
                    title="Click-through rate"
                    tooltip={
                      'The percentage of users who clicked on a link or call-to-action.\n(unique content engagement ÷ unique content views)'
                    }
                    rightSide={
                      isExperiencesLoading ? (
                        <PieGraphDeviceSkeleton legendCount={3} />
                      ) : (
                        <PieGraphDevice data={totalClickThroughRate} />
                      )
                    }
                  >
                    {isExperiencesLoading ? (
                      <Skeleton width={40} height={30} />
                    ) : (
                      <MetricValue
                        value={`${intlNumberFormat(overviewExperiencesToRender?.clickThroughRate?.value || 0)}%`}
                      >
                        {SubMetricValueDeltaChange(
                          overviewExperiencesToRender?.clickThroughRate,
                          prepareCopyForLabelBasedOn(dateRange),
                        )}
                      </MetricValue>
                    )}
                  </Metric>
                </GridLayout>
              </MetricLevel>
            </>
          )}
        </div>
      </div>

      <div className={ENGAGEMENT}>
        <div className={ENGAGEMENT_CONTAINER.SELF}>
          <div className={ENGAGEMENT_CONTAINER.HEADER}>
            <div>Engagement</div>
            <ExportEngagement />
          </div>
          <EngagementTables />
        </div>
      </div>
    </div>
  );
}
